import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { Box, Button, CircularProgress } from "@mui/material";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { createOrderApi, getOrderByIdApi, updateOrderApi } from "../api/ordersApi";
import DetailPageHeader from "../components/common/DetailPageHeader";
import StatusWorkflowStepper from "../components/common/StatusWorkflowStepper";
import OrderForm, { ORDER_FORM_ID } from "../components/orders/OrderForm";
import { TestId } from "../constants/testIds";
import type { AppDispatch, RootState } from "../store";
import { fetchAllTrucks, selectAllTrucks } from "../store/features/allTrucksSlice";
import { fetchCities } from "../store/features/citySlice";
import { fetchCustomers } from "../store/features/customerSlice";
import { fetchOperationsTeams } from "../store/features/operationsTeamSlice";
import { fetchUsers } from "../store/features/userSlice";
import type translations from "../translations/translations.json";
import {
  type Order,
  type OrderFormValues,
  OrderStatus,
  buildCreateOrderPayload,
  buildUpdateOrderPayload,
  emptyOrderFormValues,
  getAdditionalExpenses,
  isOrderEditable,
  orderToFormValues,
} from "../types/order";
import { canMutateOrders, isFinance, isUserAdmin } from "../types/user";
import { getRevisionExpenseReviewKeys } from "../utils/embeddedExpenseReview";
import { getErrorMessage } from "../utils/errorHandling";
import { ORDER_WORKFLOW_STEP_KEYS, getOrderWorkflowStepKey } from "../utils/orderStatusProgress";
import { toastEmitter } from "../utils/toastEmitter";

type TranslationKeys = keyof typeof translations.en;

const formatCreationTimestamp = (timestamp: number): string => {
  return new Date(timestamp).toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const getCreationInfo = (
  order: Order,
): { timestamp: number; userId: string; userName?: string } | null => {
  const histories = order.histories ?? [];
  if (histories.length === 0) {
    return null;
  }

  const oldest = [...histories].sort((a, b) => a.timestamp - b.timestamp)[0];
  return {
    timestamp: oldest.timestamp,
    userId: oldest.userId,
    userName: oldest.userName,
  };
};

const OrderFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { user, users } = useSelector((state: RootState) => state.user);
  const { operationsTeams } = useSelector((state: RootState) => state.operationsTeams);
  const { cities } = useSelector((state: RootState) => state.cities);
  const { customers } = useSelector((state: RootState) => state.customers);
  const trucks = useSelector(selectAllTrucks);

  const canSelectTeam = isUserAdmin(user);
  const memberTeams = useMemo(() => {
    if (!user || canSelectTeam) {
      return operationsTeams;
    }

    return operationsTeams.filter((team) => team.userIds.includes(user._id));
  }, [operationsTeams, user, canSelectTeam]);

  const defaultTeamId = useMemo(() => {
    if (isEdit || canSelectTeam || memberTeams.length === 0) {
      return "";
    }

    return memberTeams[0]._id;
  }, [canSelectTeam, isEdit, memberTeams]);

  const [initialValues, setInitialValues] = useState<OrderFormValues>(() => ({
    ...emptyOrderFormValues(),
    teamOperationsId: defaultTeamId,
  }));
  const [loadedOrder, setLoadedOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchOperationsTeams());
    dispatch(fetchUsers());
    dispatch(fetchCities());
    dispatch(fetchCustomers());
    dispatch(fetchAllTrucks());
  }, [dispatch]);

  useEffect(() => {
    if (isEdit || canSelectTeam || memberTeams.length === 0) {
      return;
    }

    setInitialValues((prev) => ({
      ...prev,
      teamOperationsId: memberTeams[0]._id,
    }));
  }, [canSelectTeam, isEdit, memberTeams]);

  useEffect(() => {
    if (!isEdit || !id) {
      return;
    }

    const loadOrder = async () => {
      setLoading(true);
      setError(null);
      try {
        const order = await getOrderByIdApi(id);
        if (!isOrderEditable(order.status)) {
          setLoadedOrder(order);
          return;
        }
        setLoadedOrder(order);
        setInitialValues(orderToFormValues(order));
      } catch (err: any) {
        setError(getErrorMessage(err, t, "fetchOrder"));
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [isEdit, id, t]);

  if (!user) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isFinance(user) || !canMutateOrders(user)) {
    return <Navigate to="/orders" replace />;
  }

  if (isEdit && loadedOrder && !isOrderEditable(loadedOrder.status)) {
    return <Navigate to={`/orders/${id}`} replace />;
  }

  const additionalExpenseIds = loadedOrder
    ? getAdditionalExpenses(loadedOrder.expenses)
        .map((expense) => expense.id)
        .filter((expenseId): expenseId is string => Boolean(expenseId))
    : [];

  const handleSubmit = async (
    values: OrderFormValues,
    extras?: { pendingProofKeys: string[]; keptProofAttachmentIds: string[] },
  ) => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = buildUpdateOrderPayload(values, {
        forUpdate: true,
        additionalExpenseIds,
      });
      const pendingProofKeys = extras?.pendingProofKeys ?? [];
      if (isEdit && id) {
        await updateOrderApi(id, {
          ...payload,
          ...(pendingProofKeys.length > 0 ? { pendingProofKeys } : {}),
          keptProofAttachmentIds: extras?.keptProofAttachmentIds ?? [],
        });
        toastEmitter.emit(t("orders.updatedSuccessfully" as TranslationKeys), "success");
        navigate(`/orders/${id}`);
      } else {
        const teamOperationsId = canSelectTeam
          ? values.teamOperationsId
          : values.teamOperationsId || defaultTeamId;
        const createPayload = {
          ...buildCreateOrderPayload(values, teamOperationsId),
          ...(pendingProofKeys.length > 0 ? { pendingProofKeys } : {}),
        };
        const order = await createOrderApi(createPayload);
        toastEmitter.emit(t("orders.createdSuccessfully" as TranslationKeys), "success");
        navigate(`/orders/${order.readableId}`);
      }
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, isEdit ? "updateOrder" : "createOrder");
      setError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const creation = loadedOrder ? getCreationInfo(loadedOrder) : null;
  const formMeta = isEdit
    ? {
        createdAtLabel: creation ? formatCreationTimestamp(creation.timestamp) : undefined,
        createdByLabel: creation?.userName || creation?.userId,
        reportReadableId: loadedOrder?.uangJalanReportReadableId,
        reportUrl: loadedOrder?.uangJalanReportReadableId
          ? `/uang-jalan-reports/${loadedOrder.uangJalanReportReadableId}`
          : undefined,
      }
    : undefined;

  const workflowSteps = ORDER_WORKFLOW_STEP_KEYS.map((key) => ({
    key,
    label: t(key as TranslationKeys),
  }));

  const revisionExpenseKeys =
    loadedOrder?.status === OrderStatus.REVISION_REQUIRED
      ? getRevisionExpenseReviewKeys(loadedOrder.expenses ?? [])
      : undefined;

  const headerActions = (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      <Button
        variant="outlined"
        onClick={() => navigate(isEdit && id ? `/orders/${id}` : "/orders")}
        disabled={submitting}
        data-testid={TestId.ORDER.CANCEL}
        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
      >
        {t("Cancel" as TranslationKeys)}
      </Button>
      <Button
        type="submit"
        form={ORDER_FORM_ID}
        variant="contained"
        disabled={submitting}
        startIcon={<SaveOutlinedIcon fontSize="small" />}
        data-testid={TestId.ORDER.SAVE}
        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
      >
        {t("Save" as TranslationKeys)}
      </Button>
    </Box>
  );

  return (
    <Box>
      <DetailPageHeader
        title={
          isEdit
            ? t("orders.form.editTitle" as TranslationKeys)
            : t("orders.form.createTitle" as TranslationKeys)
        }
        subtitle={
          isEdit && loadedOrder ? (
            <Box component="span" data-testid={TestId.ORDER.READABLE_ID}>
              {loadedOrder.readableId}
            </Box>
          ) : (
            t("orders.form.newSubtitle" as TranslationKeys)
          )
        }
        statusLabel={
          isEdit && loadedOrder ? t("orders.currentStatus" as TranslationKeys) : undefined
        }
        status={isEdit && loadedOrder ? loadedOrder.status : undefined}
        statusTestId={isEdit ? TestId.ORDER.STATUS : undefined}
        progressSlot={
          isEdit && loadedOrder ? (
            <StatusWorkflowStepper
              steps={workflowSteps}
              activeStepKey={getOrderWorkflowStepKey(loadedOrder.status)}
            />
          ) : undefined
        }
        actions={headerActions}
      />

      <OrderForm
        key={isEdit ? id : `new-${defaultTeamId}`}
        initialValues={initialValues}
        teams={canSelectTeam ? operationsTeams : memberTeams}
        cities={cities}
        trucks={trucks}
        users={users}
        customers={customers}
        isEdit={isEdit}
        canSelectTeam={canSelectTeam}
        loading={submitting}
        error={error}
        meta={formMeta}
        revisionExpenseKeys={revisionExpenseKeys}
        additionalExpenseIds={additionalExpenseIds}
        hideActions
        existingProofAttachments={loadedOrder?.proofAttachments ?? []}
        onSubmit={handleSubmit}
        onCancel={() => navigate(isEdit && id ? `/orders/${id}` : "/orders")}
      />
    </Box>
  );
};

export default OrderFormPage;
